import { Card, Meter, categoricalSlotByIndex } from "@bfc-software/pallet";

import { percentOf, type ZoneProgress } from "../-data/dashboard-model";
import type { DashboardT } from "./dashboard-t";
import styles from "./dashboard.module.css";

/**
 * Units picked vs. total, one bar per zone.
 *
 * Each zone's bar is its own colour so the eye can tell them apart, taken from
 * the categorical ramp by position in the list: the list is ordered by name, so
 * a zone keeps its colour from one refresh to the next, and the first six are
 * guaranteed distinct (an id-derived colour would not be). The unzoned bucket is
 * the neutral idle tone, since it is not a zone. The figure sits under the bar.
 */
export function ZoneProgressCard({
  zones,
  t,
}: {
  zones: ReadonlyArray<ZoneProgress>;
  t: DashboardT;
}) {
  return (
    <Card
      variant="panel"
      layout="body-only"
      data-test-role="dashboard-zones"
      body={
        <>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>
              {t("dashboard.zones.title")}
            </span>
            <span className={styles.panelHint}>
              {t("dashboard.zones.hint")}
            </span>
          </div>
          {zones.length === 0 && (
            <p className={styles.note}>{t("dashboard.zones.empty")}</p>
          )}
          <div className={styles.zones}>
            {zones.map((z, index) => (
              <div
                key={z.id}
                className={styles.zone}
                data-zone={z.unzoned ? "unzoned" : z.zone}
              >
                <span className={styles.zoneName}>
                  {z.unzoned ? t("dashboard.zones.unzoned") : z.zone}
                </span>
                <Meter
                  done={z.picked}
                  total={z.total}
                  size="wide"
                  layout="stacked"
                  {...(z.unzoned
                    ? { tone: "idle" as const }
                    : { slot: categoricalSlotByIndex(index) })}
                  label={t("dashboard.zones.row", {
                    picked: z.picked,
                    total: z.total,
                    percent: percentOf(z.picked, z.total),
                  })}
                />
              </div>
            ))}
          </div>
        </>
      }
    />
  );
}
